import { useState, useEffect } from "react";
import { X, Camera, ZoomIn, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getProjetoFotos, type ProjetoFoto } from "../services/ataImagem.service";

interface FotoHistoricoDrawerProps {
  open: boolean;
  onClose: () => void;
  projetoId: string;
}

interface FotoGroup {
  ata_numero: number;
  ata_data: string;
  ata_nome: string | null;
  fotos: ProjetoFoto[];
}

function formatData(raw: string | null | undefined, lang: string): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(lang, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function FotoHistoricoDrawer({ open, onClose, projetoId }: FotoHistoricoDrawerProps) {
  const [fotos, setFotos] = useState<ProjetoFoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState<ProjetoFoto | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const { t, i18n } = useTranslation('common');

  useEffect(() => {
    if (!open || !projetoId) return;
    setLoading(true);
    getProjetoFotos(projetoId)
      .then(setFotos)
      .catch(() => setFotos([]))
      .finally(() => setLoading(false));
  }, [open, projetoId]);

  if (!open) return null;

  // Agrupar fotos por ata
  const groups: FotoGroup[] = [];
  for (const foto of fotos) {
    const existing = groups.find((g) => g.ata_numero === foto.ata_numero);
    if (existing) {
      existing.fotos.push(foto);
    } else {
      groups.push({
        ata_numero: foto.ata_numero,
        ata_data: foto.ata_data,
        ata_nome: foto.ata_nome,
        fotos: [foto],
      });
    }
  }

  const toggleCollapse = (ataNumero: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(ataNumero)) next.delete(ataNumero);
      else next.add(ataNumero);
      return next;
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-background border-l border-border shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">{t('photos_drawer.title')}</h2>
            {fotos.length > 0 && (
              <span className="text-xs text-muted-foreground font-normal">
                ({fotos.length} {fotos.length === 1 ? t('photos_drawer.photo_one') : t('photos_drawer.photo_other')})
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-md transition-colors"
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && fotos.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Camera size={28} className="text-muted-foreground/40" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">{t('photos_drawer.empty_title')}</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {t('photos_drawer.empty_description')}
              </p>
            </div>
          )}

          {!loading && groups.length > 0 && (
            <div className="divide-y divide-border">
              {groups.map((group) => {
                const isCollapsed = collapsed.has(group.ata_numero);
                return (
                  <div key={group.ata_numero}>
                    {/* Group header */}
                    <button
                      className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors text-left"
                      onClick={() => toggleCollapse(group.ata_numero)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-bold flex-shrink-0">
                          {group.ata_numero}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {group.ata_nome ? group.ata_nome : t('photos_drawer.meeting_label', { number: group.ata_numero })}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatData(group.ata_data, i18n.language)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {group.fotos.length} {group.fotos.length === 1 ? t('photos_drawer.photo_one') : t('photos_drawer.photo_other')}
                        </span>
                        {isCollapsed ? (
                          <ChevronDown size={14} className="text-muted-foreground" />
                        ) : (
                          <ChevronUp size={14} className="text-muted-foreground" />
                        )}
                      </div>
                    </button>

                    {/* Photos grid */}
                    {!isCollapsed && (
                      <div className="grid grid-cols-3 gap-1.5 px-6 pb-4">
                        {group.fotos.map((foto) => (
                          <div
                            key={foto.id}
                            className="group relative aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer"
                            onClick={() => setLightbox(foto)}
                          >
                            <img
                              src={foto.url}
                              alt={foto.descricao || `Foto ${foto.id}`}
                              className="w-full h-full object-cover transition-all duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 flex items-center justify-center">
                              <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 drop-shadow-md" />
                            </div>
                            {foto.descricao && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                                <p className="text-white text-[10px] leading-tight line-clamp-2">
                                  {foto.descricao}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 p-3 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex flex-col items-center max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.url}
              alt={lightbox.descricao || t('photos_drawer.enlarged_alt')}
              className="max-w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="mt-4 text-center">
              <p className="text-white/60 text-sm">
                Ata {lightbox.ata_numero}
                {lightbox.ata_data ? ` • ${formatData(lightbox.ata_data, i18n.language)}` : ""}
              </p>
              {lightbox.descricao && (
                <p className="text-white/80 text-sm font-light mt-1">{lightbox.descricao}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
